import React, {
  forwardRef,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
  Menu,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Alert,

} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';

import { calcMinimumQuantityFromMarginResponse } from "../../hooks/useOrderFormCore";
import useMarginCalculator from "../../hooks/useMarginCalculator";
import { useOrderForm, calcMargin } from "../../hooks/useOrderForm";
import { extractSymbolForMargin } from "../../hooks/useOrderFormUtils";
import octopusInstance from "../../services/octopus/octopusInstance";
import { getExchangeCode, exchangeSpecificStringifiedNumber } from "../../services/exchanges";
import { formatPriceWithPrecision } from "../../utils/utilfunction";

import { useSelector, useDispatch } from "react-redux";
import { addMarketWatchInstrumentAction, deleteMarketWatchInstrumentAction } from "../../store/actions/watchlist";
// import { fetchFundsView } from "../../store/actions/funds";
import { calculateBrokerageValueAsync } from "../../utils/brokerageUtils";

import { enqueueSnackbar } from "notistack";
// import { DEPOSIT_URL } from "../../utils/constant";
import useBalance from "../Portfolio/Positions/useBalance";
import { updateCalculatedFunds } from "../../store/actionCreators/fundsActions";
import { usePendingOrdersInOrderWindow } from "../../hooks/usePendingOrdersInOrderWindow";
import PendingOrdersSection from "./PendingOrdersSection";
import { closeOrderWindow } from "../../store/actions/orders";
// import { fetchScripInfo } from "../../services/backendAPI";
import OrderWindowHeader from "./parts/OrderWindowHeader";
import MarketDataPanel from "./parts/MarketDataPanel";
import ProductTypeTabs from "./parts/ProductTypeTabs";
import OrderTypeTabs from "./parts/OrderTypeTabs";
import FundsSummary from "./parts/FundsSummary";
import QuantityPriceInputs from "./parts/QuantityPriceInputs";
import WatchlistBookmark from "./parts/WatchlistBookmark";
import FundsMenuOverlay from "../Common/FundsMenuOverlay";
import WithdrawModal from "../Profile/WithdrawModal";
import AddFundsModal from "../Common/AddFundsModal";
import PaymentGuidelinesModal from "../Common/PaymentGuidelinesModal";

const DraggablePaper = forwardRef((props, ref) => {
  return (
    <Draggable
      handle=".draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={ref} />
    </Draggable>
  );
});

const OrderWindow = React.memo((props) => {
  const theme = useTheme();
  // const {formattedWatchlists} = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const positionData = useSelector((state) => state.portfolio.arrPositionsUpdateData);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isSquareOff, setIsSquareOff] = useState(false);
  const initialLTP = useRef(null);
  const currentLTP = useRef(null);
  const isFirstMessage = useRef(true);
  const [requiredMargin, setRequiredMargin] = useState(0);
  const [brokerageValue, setBrokerageValue] = useState(0);
  // const userSettings = useSelector((state) => state.profile.userSettings);
  const isBrokerageApplied = useSelector(state => state.brokerage.brokerageData.is_brokerage_applied);


  // Debug: brokerage toggle status
  // console.log("isBrokerageApplied", isBrokerageApplied);
  const { userData } = useSelector(state => state.profile);
  const marginConfig = useSelector(state => state.margin.marginConfig);
  const brokerageData = useSelector(state => state.brokerage.brokerageData);

  // Get funds data using the global useBalance hook
  const balanceData = useBalance();
  const [loadingFunds, setLoadingFunds] = useState(false);

  const [marketData, setMarketData] = useState({
    ltp: props.ltp,
    ltt: props.ltt, // Add LTT to state
    bestBid: props.bestBid,
    bestAsk: props.bestAsk,
  });


  // const navigatedToPayIn = useSelector((state) => state.funds.navigatedToPayIn);

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  // Funds Overlay and Modals State
  const [fundsPopoverAnchor, setFundsPopoverAnchor] = useState(null);
  const [mobileFundsOpen, setMobileFundsOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false);
  const [isPaymentGuidelinesModalOpen, setIsPaymentGuidelinesModalOpen] = useState(false);
  const payInComponentRef = useRef(null);

  // Get available cash from useBalance hook
  const availableMargin = balanceData.availableCash;
  const isLowMargin = availableMargin < requiredMargin;
  // Handle refresh funds
  const handleRefreshFunds = () => {
    setLoadingFunds(true);
    dispatch(updateCalculatedFunds());
    setTimeout(() => setLoadingFunds(false), 1000); // Ensure loading state is visible for at least 1 second
  };

  // Handle add funds click (Option B: open local modal instead of navigating)
  const handleAddFundsClick = () => {
    if (!userData?.phone_number) {
      enqueueSnackbar('User phone number not available', { variant: 'error' });
      return;
    }
    setIsAddFundsModalOpen(true);
  };

  const handleAvailableClick = (e) => {
    if (isMobile) {
      setMobileFundsOpen(true);
    } else {
      setFundsPopoverAnchor(e.currentTarget);
    }
  };



  // useEffect(() => {
  //   // Fetch funds when component mounts
  //   dispatch(fetchFundsView());
  // }, [dispatch]);

  useEffect(() => {
    if (props.open) {
      isFirstMessage.current = true;
      initialLTP.current = null;
      currentLTP.current = null;
    }
  }, [props.open]);

  // Helper function to format WebSocket price data with precision
  const formatWebSocketPrice = useCallback((value, props) => {
    if (!value) return value;

    // Apply price precision formatting for CDS exchange
    const pricePrecision = props.price_precision || props.payload?.price_precision;

    if ((props.exchange === 'CDS' || props.exchange === "DCSX") && pricePrecision !== undefined) {
      const formattedValue = formatPriceWithPrecision(value, pricePrecision, {
        showCurrency: false,
        showCommas: false,
        removeTrailingZeros: false
      });
      return formattedValue;
    }

    return value;
  }, [props.exchange, props.price_precision, props.payload?.price_precision]);

  const handleWebSocketMessage = useCallback(({ msg }) => {
    if (isFirstMessage.current && msg.lastTradedPrice) {
      initialLTP.current = formatWebSocketPrice(msg.lastTradedPrice, props);
      isFirstMessage.current = false;
    }
    currentLTP.current = msg.lastTradedPrice || currentLTP.current;

    setMarketData((prev) => {
      const newData = {
        ltp: formatWebSocketPrice(msg.lastTradedPrice, props) || prev.ltp,
        ltt: msg.lastTradedTime || prev.ltt, // Update LTT
        bestBid: formatWebSocketPrice(msg.bestBidPrice, props) || prev.bestBid,
        bestAsk: formatWebSocketPrice(msg.bestAskPrice, props) || prev.bestAsk,
      };
      if (JSON.stringify(prev) === JSON.stringify(newData)) {
        return prev;
      }
      return newData;
    });
  }, [formatWebSocketPrice]);

  useEffect(() => {
    if (!props.open || !props.token || !props.exchange) return;
    const handler = octopusInstance.wsHandler({
      messageType: "DetailMarketDataMessage",
      subscriptionLocation: `orderwindow-${props.token}`,
      payload: {
        exchangeCode: getExchangeCode(props.exchange),
        instrumentToken: props.token,
      },
    });

    handler.subscribe(handleWebSocketMessage);
    return () => handler.unsubscribe();
  }, [props.open, props.token, props.exchange, handleWebSocketMessage]);

  const {
    orderType,
    quantity,
    price,
    loading,
    product,
    is_modify,
    priceError,
    quantityError,
    orderSide,
    setOrderType,
    setProduct,
    handleTypeChange,
    handlePriceChange,
    handleTriggerChange,
    handleQuantityChange,
    handleQuantityKeyDown,
    handlePriceKeyDown,
    handleSubmit,
    handleModify,
    hasMatchingPosition,
    isDisabled,
    getProductOptions,
    radioTypeOptions,
    strike_price,
    minimumQtyRequired,
    minimumOrderValue,
    isSquareOffOrder,
    existingPositionQty,
    PRIME_SETTINGS,
  } = useOrderForm({
    ...props,
    ltp: initialLTP.current,
    bestBid: marketData.bestBid,
    bestAsk: marketData.bestAsk,
  });

  const masterSettings = useSelector(state => state.profile.masterSettings) || {};
  const configValue = masterSettings?.scrip_allow_margin_config?.[props.exchange];
  const useServerSideMargin = configValue === true || configValue === 'true';
  const brokerSettings = useSelector(state => state.profile.brokerSettings || {});

  const serverSideMarginObject = useMemo(() => {
    if (!useServerSideMargin) return null;
    return hasMatchingPosition(positionData, props.token, orderSide, quantity, product);
  }, [useServerSideMargin, positionData, props.token, orderSide, quantity, product, hasMatchingPosition]);

  const {
    requiredMargin: apiRequiredMargin,
    marginData,
    isAllowed: marginIsAllowed,
    errorMessage: marginErrorMessage,
    isLoading: marginLoading,
    isStale: marginIsStale,
    hasApiError: marginHasApiError,
    refresh: refreshMargin,
  } = useMarginCalculator({
    enabled: useServerSideMargin && !serverSideMarginObject?.hideMargin,
    isOpen: props.open,
    exchange: props.exchange,
    instrument_type: props.payload?.instrument_name,
    product,
    orderSide,
    orderType,
    token: props.token,
    underlying_token: props.payload?.underlying_token,
    trading_symbol: props.trading_symbol,
    quantity: useServerSideMargin && serverSideMarginObject ? serverSideMarginObject.netQuantity : quantity,
    price,
    bestBid: marketData.bestBid,
    bestAsk: marketData.bestAsk,
    ltp: marketData.ltp,
    lot_size: props.lotsize,
  });

  // Sync API-driven margin into local state so existing downstream consumers keep working.
  // Skipped while the API call is failing so the local calcMargin fallback effect owns state instead.
  useEffect(() => {
    if (!useServerSideMargin || marginHasApiError) return;

    if (serverSideMarginObject?.hideMargin) {
      setRequiredMargin(0);
    } else {
      setRequiredMargin(Number(apiRequiredMargin) || 0);
    }
  }, [useServerSideMargin, marginHasApiError, apiRequiredMargin, serverSideMarginObject]);

  const blockedByMargin = useServerSideMargin && !marginHasApiError && marginIsAllowed === false;

  // Minimum quantity comes from the margin API response when that API is in use, otherwise from
  // the config-based calculation in useOrderForm (also the fallback when the API fails).
  const effectiveMinimumQty = useMemo(() => {
    if (!useServerSideMargin || marginHasApiError) return minimumQtyRequired;

    // No response yet: no minimum. Submit stays disabled while the margin is loading.
    if (!marginData) return 0;

    const baseMinQty = calcMinimumQuantityFromMarginResponse(marginData);
    if (baseMinQty === null) return minimumQtyRequired;

    // Same position rules as useOrderForm: none for a square-off, and an existing
    // same-side position counts towards the minimum.
    if (props.payload?.isSquareOff || isSquareOffOrder) return 0;
    return Math.max(0, baseMinQty - existingPositionQty);
  }, [useServerSideMargin, marginHasApiError, marginData, minimumQtyRequired, props.payload?.isSquareOff, isSquareOffOrder, existingPositionQty]);

  useEffect(() => {
    async function updateBrokerage() {
      const value = await calculateBrokerageValueAsync({
        exchange: props.exchange,
        token: props.token,
        brokerageData,
        quantity,
        price,
        orderType,
        orderSide,
        marketData,
        instrument_name: props.instrument_name,
      });
      setBrokerageValue(value);
    }
    updateBrokerage();
  }, [props.exchange, props.token, brokerageData, quantity, price, orderType, orderSide, marketData, props.instrument_name]);
  // Use custom hook for pending orders (after useOrderForm to access orderSide, product, etc.)
  const pendingOrders = usePendingOrdersInOrderWindow({
    token: props.token,
    exchange: props.exchange,
    isOpen: props.open,
    orderSide: orderSide,
    product: product,
    positionData: positionData,
    hasMatchingPosition: hasMatchingPosition
  });
  // console.log("pendingOrders", pendingOrders);

  // Update isSquareOff state based on all square-off detection methods
  useEffect(() => {
    // Method 1: Direct square-off from props
    const isDirectSquareOff = props.isSquareOff || false;

    // Method 2: Square-off detected from useOrderForm hook
    const isPositionBasedSquareOff = isSquareOffOrder;

    // Method 3: Manual position check for additional validation
    let isManualSquareOff = false;
    if (!isDirectSquareOff && !isPositionBasedSquareOff && positionData && positionData.length > 0) {
      const matchingPosition = positionData.find(position =>
        (String(position.token) === String(props.token) ||
          String(position.instrument_token) === String(props.token)) &&
        position.net_quantity !== 0 &&
        position.product === product
      );

      if (matchingPosition) {
        const netQuantity = Number(matchingPosition.net_quantity) || 0;
        const positionSide = netQuantity < 0 ? "SELL" : "BUY";
        const numericQty = parseInt(quantity) || 0;

        // If this is a square-off order (opposite direction of position)
        if ((positionSide === "BUY" && orderSide === "SELL") ||
          (positionSide === "SELL" && orderSide === "BUY")) {

          // If entered quantity is less than or equal to position quantity, it's a valid square-off
          if (numericQty <= Math.abs(netQuantity)) {
            isManualSquareOff = true;
          }
        }
      }
    }

    // Set the final square-off state
    const finalSquareOffState = isDirectSquareOff || isPositionBasedSquareOff || isManualSquareOff;

    if (finalSquareOffState !== isSquareOff) {
      // console.log("Updating isSquareOff state:", { from: isSquareOff, to: finalSquareOffState });
      setIsSquareOff(finalSquareOffState);
    }
  }, [props.isSquareOff, isSquareOffOrder, positionData, props.token, product, orderSide, quantity, isSquareOff]);

  // Flag to check if quantity meets minimum requirements
  const [minQuantityError, setMinQuantityError] = useState(false);

  // Update the minQuantityError calculation to respect square-off operations
  useEffect(() => {
    // If this is a square-off operation, bypass minimum quantity validation
    if (isSquareOff) {
      setMinQuantityError(false);
      return;
    }

    // Normal validation logic for non-square-off orders
    if (effectiveMinimumQty > 0 && parseInt(quantity) < effectiveMinimumQty) {
      // For market orders, we've already calculated minimum quantity based on market price
      // For limit orders, check only if price is set
      if (orderType === "MARKET" || orderType === "STOPLOSSMARKET" || parseFloat(price) > 0) {
        setMinQuantityError(true);
      } else {
        setMinQuantityError(false);
      }
    } else {
      setMinQuantityError(false);
    }
  }, [quantity, effectiveMinimumQty, price, orderType, isSquareOff, orderSide, product]);

  // Cache previous values to avoid unnecessary margin calculations
  const marginCalculationCache = useRef({
    lastInputs: null,
    lastResult: 0
  });

  // Check if document is visible
  const isDocumentVisible = () => document.visibilityState === 'visible';

  useEffect(() => {
    // New margin-API flow owns the requiredMargin state; skip legacy client-side calc,
    // unless the API is currently failing, in which case this silently takes over as a fallback.
    if (useServerSideMargin && !marginHasApiError) return;

    if (marginHasApiError) {
      console.warn("calcMargin: falling back to local margin calculation because the margin API failed");
    }

    let marginPrice;

    // Skip calculation if document is not visible
    if (!isDocumentVisible() || !quantity || !product || !props.exchange) {
      return;
    }

    if (orderType === "LIMIT" || orderType === "SL") {
      marginPrice = price;
    } else {
      marginPrice = orderSide === "BUY" ? marketData.bestAsk : marketData.bestBid;
    }

    if (!marginPrice || Number(marginPrice) === 0) {
      marginPrice = marketData.ltp || props.payload?.price || props.payload?.ltp || marginPrice || 0;
    }

    // Create a key from all inputs that affect margin calculation
    const inputKey = JSON.stringify({
      quantity,
      product,
      orderSide,
      marginPrice,
      exchange: props.exchange,
      token: props.token,
      instrumentName: props.payload?.instrument_name,
      raw_board_lot_quantity: props.payload?.raw_board_lot_quantity
    });

    // Check if we've already calculated this exact margin
    if (marginCalculationCache.current.lastInputs === inputKey) {
      return; // No need to recalculate
    }

    const requiredMarginObject = hasMatchingPosition(positionData, props.token, orderSide, quantity, product);
    // console.log("requiredMarginObject", requiredMarginObject);
    // Calculate margin using dynamic config first, fallback to static config
    const symbol = extractSymbolForMargin(props.exchange, props.trading_symbol, props.payload);

    // console.log("Symbol extraction:", { exchange: props.exchange, extractedSymbol: symbol });
    const margin = calcMargin({
      order_config: PRIME_SETTINGS,
      exchange: props.exchange,
      orderSide,
      product,
      price: parseFloat(marginPrice || 0),
      qty: Number(requiredMarginObject?.netQuantity),
      instrumentName: props.payload?.instrument_name,
      raw_board_lot_quantity: props.payload?.raw_board_lot_quantity,
      marginConfig: marginConfig,
      symbol: symbol,
      instrumentDetails: props.payload
    });
    // Store result in cache
    marginCalculationCache.current = {
      lastInputs: inputKey,
      lastResult: margin
    };

    // Update the state with the new margin
    if (requiredMarginObject?.hideMargin) {
      setRequiredMargin(0);
    } else {
      setRequiredMargin(margin);
    }
  }, [
    quantity,
    product,
    orderSide,
    orderType,
    price,
    strike_price,
    marketData,
    positionData,
    props.exchange,
    props.token,
    props.payload,
    brokerageData,
    hasMatchingPosition,
    marginConfig,
    PRIME_SETTINGS,
    props.display_name,
    props.trading_symbol,
    useServerSideMargin,
    marginHasApiError
  ]);

  // Clear cache when visibility changes to ensure fresh data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Reset cache when tab becomes visible
        marginCalculationCache.current = {
          lastInputs: null,
          lastResult: 0
        };
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const { onClose: onCloseProp } = props;
  const handleClose = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      if (onCloseProp) {
        onCloseProp();
      }
    },
    [onCloseProp]
  );

  // Add a function to handle adding instruments to marketwatch
  const handleAddToMarketwatch = useCallback((watchlist, index) => {
    // Check if watchlist is full
    if (watchlist.instruments && watchlist.instruments.length >= 20) {
      enqueueSnackbar(`${watchlist.name} is full (max 20 instruments)`, {
        variant: "warning",
      });
      return;
    }

    // Create payload
    const payload = {
      mw_id: watchlist.id,
      exchange: props.exchange,
      trading_symbol: props.trading_symbol,
      token: props.token.toString(),
      execution: "NORMAL",
    };


    // Dispatch action with correct parameters
    dispatch(addMarketWatchInstrumentAction(index, payload))
      .then(response => {
        // console.log("Add to marketwatch response:", response);
        if (response && response.status === "success") {
          enqueueSnackbar(`${props.display_name || props.trading_symbol} added to ${watchlist.name}`, {
            variant: "success",
            autoHideDuration: 1000,
          });
        } else {
          enqueueSnackbar(response?.message || "Failed to add to marketwatch", {
            variant: "error",
            autoHideDuration: 1000,
          });
        }
      });
  }, [dispatch, props.display_name, props.exchange, props.token, props.trading_symbol]);

  // Add a function to handle removing instruments from marketwatch
  const handleRemoveFromMarketwatch = useCallback((watchlist, index) => {
    const payload = {
      marketWatchId: watchlist.id,
      exchange: props.exchange,
      token: props.token,
      symbolName: props.trading_symbol,
      execution: props.execution || "REGULAR",
    };

    dispatch(deleteMarketWatchInstrumentAction(
      payload,
      index,
      props.formattedWatchlists,
      { token: props.token, exchange: props.exchange, trading_symbol: props.trading_symbol }
    ))
      .then(response => {
        if (response && response.status === "success") {
          enqueueSnackbar(`${props.display_name || props.trading_symbol} removed from ${watchlist.name}`, {
            variant: "success",
            autoHideDuration: 1000,
          });
        } else {
          enqueueSnackbar(response?.message || "Failed to remove from marketwatch", {
            variant: "error",
            autoHideDuration: 1000,
          });
        }
      });
  }, [dispatch, props.display_name, props.exchange, props.formattedWatchlists, props.token, props.trading_symbol, props.execution]);

  // Show warning message if funds are insufficient (computed inline where needed)

  // Format currency value
  const formatCurrency = (value) => {
    if (!value) return "₹0";
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter.format(value);
  };

  // Format bid/ask prices with precision
  const formatBidAskPrice = (value) => {
    if (!value) return null;

    // Apply price precision formatting for CDS exchange with trailing zeros
    // Check both top level props and payload for price_precision
    const pricePrecision = props.price_precision || props.payload?.price_precision;

    if ((props.exchange === 'CDS' || props.exchange === 'DCSX') && pricePrecision !== undefined) {
      return formatPriceWithPrecision(value, pricePrecision, {
        showCurrency: false,
        showCommas: false,
        removeTrailingZeros: false
      });
    }

    return exchangeSpecificStringifiedNumber(props.exchange, value);
  };

  // Update isDisabled to include minQuantityError and pending orders for square-off
  // (kept inline in button disabled prop to avoid extra memo and unused var)

  // Add console logs to detect square-off mode
  useEffect(() => {
    // Log on component mount to confirm square-off state
    // console.log("OrderWindow square-off state:", { isSquareOff, propsIsSquareOff: props.isSquareOff });

    // For square-off operations, ensure error is cleared
    if (isSquareOff) {
      setMinQuantityError(false);
    }
  }, [isSquareOff, props.isSquareOff, minQuantityError, quantity, minimumQtyRequired]);

  // Helper to parse LTT
  const getLttInMs = (ltt) => {
    if (!ltt) return 0;
    return ltt.toString().length === 10 ? ltt * 1000 : ltt;
  };

  const { ltt_feature = 'false', ltt_time_in_seconds } = brokerSettings;

  const handleOrderSubmit = (e) => {
    e.stopPropagation();

    if (useServerSideMargin) {

      if (blockedByMargin) {
        enqueueSnackbar(marginErrorMessage || "Order not allowed for this instrument.", {
          variant: "error",
          autoHideDuration: 4000,
        });
        return;
      }
    }

    const ltt = marketData.ltt;


    if (ltt && ltt_feature === 'true') {
      const lttMs = getLttInMs(ltt);
      const now = Date.now();
      const allowedTimeDiff = ltt_time_in_seconds * 1000;

      if (now - lttMs > allowedTimeDiff) {
        enqueueSnackbar(`Order blocked due to stale market price. Please wait for the updated price.`, {
          variant: "error",
          autoHideDuration: 4000
        });
        return;
      }
    }

    if (is_modify) {
      handleModify(e);
    } else {
      handleSubmit(e);
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      PaperComponent={isMobile ? Paper : DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      keepMounted
      onMouseLeave={(e) => e.stopPropagation()}
      onBlur={(e) => e.stopPropagation()}
    >
      <DialogTitle
        className={!isMobile ? "draggable-dialog-title" : undefined}
        sx={{
          backgroundColor:
            orderSide === "BUY"
              ? theme.palette.buy.background
              : theme.palette.sell.background,
          background:
            orderSide === "BUY"
              ? `linear-gradient(135deg, ${theme.palette.buy.background} 0%, ${theme.palette.buy.light} 100%)`
              : `linear-gradient(135deg, ${theme.palette.sell.background} 0%, ${theme.palette.sell.light} 100%)`,
          color: theme.palette.common.white,
          cursor: !isMobile ? "move" : "default",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: isMobile ? 1.5 : 2,
          boxShadow: theme.shadows[4],
          transition: "background-color 0.3s ease",
        }}
      >
        <OrderWindowHeader
          isMobile={isMobile}
          orderSide={orderSide}
          onTypeChange={handleTypeChange}
          isModify={props.is_modify || props.isSquareOff}
          type={props.type}
          displayName={props.display_name || props.symbol}
          onClose={handleClose}
        />
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          px: isMobile ? 1.5 : 2,
          height: isMobile ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
          overflow: isMobile ? "auto" : "visible",
          // Ensure scrolling works on mobile
          [theme.breakpoints.down('sm')]: {
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: "calc(100vh - 120px)", // Account for header and footer
          }
        }}
      >
        {/* Only show warning message if funds are insufficient */}
        {availableMargin < requiredMargin && requiredMargin > 0 && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              mb: 2,
              fontWeight: 500,
              borderRadius: 2,
              textAlign: isMobile ? 'center' : 'left',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              fontSize: isMobile ? '1rem' : '0.95rem',
            }}
          >
            <strong>Insufficient funds:</strong> You do not have enough balance to place this order.
          </Alert>
        )}

        {/* Market Data Section */}
        <MarketDataPanel
          bestBid={marketData.bestBid}
          ltp={marketData.ltp}
          ltt={marketData.ltt}
          bestAsk={marketData.bestAsk}
          formatValue={formatBidAskPrice}
        />

        {/* Pending Orders Section */}
        {isSquareOff && !is_modify && (
          <Box
            sx={{
              width: '100%',
              mb: 2,
              // Add some debug styling to ensure visibility
              [theme.breakpoints.down('sm')]: {
                border: '1px solid transparent', // Debug border
                borderRadius: 1,
                backgroundColor: 'background.paper',
                position: 'relative',
                zIndex: 1,
              }
            }}
          >
            {/* Debug log removed for cleaner console */}
            <PendingOrdersSection
              orders={pendingOrders.instrumentPendingOrders}
              expanded={pendingOrders.expanded}
              onToggle={pendingOrders.toggleExpanded}
              onCancel={pendingOrders.cancelOrder}
              cancelling={pendingOrders.cancelling}
              isSquareOffOrder={pendingOrders.isSquareOffOrder}
              stopLossOrdersCount={pendingOrders.stopLossOrdersCount}
            />
          </Box>
        )}
        {/* Configuration Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Product Type and Order Type Selector */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              alignItems: "center",
            }}
          >
            {/* Product Type - Hidden for CDS exchange */}
            {props.exchange !== "CDS" && props.exchange !== "DCSX" && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <ProductTypeTabs
                  value={product}
                  onChange={setProduct}
                  disabled={props.is_modify || props.isSquareOff}
                  getOptions={getProductOptions}
                />
              </Box>
            )}

            {/* Order Type */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <OrderTypeTabs
                value={orderType}
                onChange={setOrderType}
                currentPrice={props.is_modify ? (props.payload?.price || props.price || initialLTP.current) : initialLTP.current}
                handlePriceChange={handlePriceChange}
                handleTriggerChange={handleTriggerChange}
                radioTypeOptions={radioTypeOptions}
              />
            </Box>
          </Box>

          {/* Quantity, Price, and Trigger Price Inputs */}
          <Grid container spacing={1}>
            {!isMobile ? (
              <>
                <Grid item xs={12} container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const sanitizedValue = inputValue.replace(/^0+/, "");

                        if (
                          sanitizedValue === "" ||
                          /^\d+$/.test(sanitizedValue)
                        ) {
                          handleQuantityChange({
                            target: {
                              value: sanitizedValue || "0",
                              name: e.target.name,
                            },
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e" || e.key === "E") {
                          e.preventDefault();
                        }
                        handleQuantityKeyDown(e);
                      }}
                      error={quantityError || (minQuantityError && !isSquareOff)}
                      helperText={
                        quantityError
                          ? `Invalid quantity`
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mr: 1 }}
                            >
                              Qty
                            </Typography>
                          </InputAdornment>
                        ),
                        inputProps: {
                          step: props.exchange === "MCX" ? 1 : props.lotsize,
                          min: props.exchange === "MCX" ? 1 : props.lotsize,
                          style: {
                            textAlign: "left",
                            fontWeight: "bold",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: (quantityError || (minQuantityError && !isSquareOff))
                              ? theme.palette.error.main
                              : alpha(theme.palette.text.secondary, 0.3),
                          },
                          "&:hover fieldset": {
                            borderColor: (quantityError || (minQuantityError && !isSquareOff))
                              ? theme.palette.error.dark
                              : theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                    {minQuantityError && !isSquareOff && (
                      <Box
                        sx={{
                          mt: 0.5,
                          px: 1,
                          py: 0.5,
                          backgroundColor: alpha(theme.palette.error.light, 0.1),
                          borderRadius: 1,
                          border: `1px dashed ${alpha(theme.palette.error.main, 0.3)}`,
                        }}
                      >
                        <Typography variant="caption" color="error" display="block" fontWeight="medium">
                          Min. Purchase Amount: {formatCurrency(minimumOrderValue)}
                        </Typography>
                        <Typography variant="caption" color="error" display="block" fontWeight="medium">
                          Min. Quantity: {effectiveMinimumQty}
                        </Typography>
                        {/* {(orderType === "MARKET" || orderType === "STOPLOSSMARKET") && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Based on current market price: {formatCurrency(orderSide === "BUY" ? marketData.bestAsk || marketData.ltp : marketData.bestBid || marketData.ltp)}
                          </Typography>
                        )} */}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Price"
                      type="number"
                      value={price || ""}
                      onChange={handlePriceChange}
                      onKeyDown={handlePriceKeyDown}
                      error={priceError}
                      helperText={
                        priceError
                          ? `Value must be a multiple of tick_size (${props.tick_size})`
                          : ""
                      }
                      disabled={
                        orderType === "MARKET" || orderType === "STOPLOSSMARKET"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mr: 1 }}
                            >
                              ₹
                            </Typography>
                          </InputAdornment>
                        ),
                        inputProps: {
                          step: props.tick_size,
                          min: 0,
                          style: {
                            textAlign: "left",
                            fontWeight: "bold",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: theme.palette.text.disabled,
                          fontWeight: "bold",
                          backgroundColor: alpha(
                            theme.palette.action.disabledBackground,
                            0.1
                          ),
                          borderRadius: 1,
                          opacity: 0.7,
                          cursor: "not-allowed",
                          textDecoration: "line-through",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled": {
                          "& > fieldset": {
                            borderColor: alpha(
                              theme.palette.text.disabled,
                              0.3
                            ),
                            borderStyle: "dashed",
                            borderWidth: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: priceError
                              ? theme.palette.error.main
                              : alpha(theme.palette.text.secondary, 0.3),
                          },
                          "&:hover fieldset": {
                            borderColor: priceError
                              ? theme.palette.error.dark
                              : theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const sanitizedValue = inputValue.replace(/^0+/, "");

                      if (
                        sanitizedValue === "" ||
                        /^\d+$/.test(sanitizedValue)
                      ) {
                        handleQuantityChange({
                          target: {
                            value: sanitizedValue || "0",
                            name: e.target.name,
                          },
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                      handleQuantityKeyDown(e);
                    }}
                    error={quantityError || (minQuantityError && !isSquareOff)}
                    helperText={
                      quantityError
                        ? `Invalid quantity`
                        : ""
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            Qty
                          </Typography>
                        </InputAdornment>
                      ),
                      inputProps: {
                        step: props.exchange === "MCX" ? 1 : props.lotsize,
                        min: props.exchange === "MCX" ? 1 : props.lotsize,
                        style: {
                          textAlign: "left",
                          fontWeight: "bold",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: (quantityError || (minQuantityError && !isSquareOff))
                            ? theme.palette.error.main
                            : alpha(theme.palette.text.secondary, 0.3),
                        },
                        "&:hover fieldset": {
                          borderColor: (quantityError || (minQuantityError && !isSquareOff))
                            ? theme.palette.error.dark
                            : theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  {minQuantityError && !isSquareOff && (
                    <Box
                      sx={{
                        mt: 0.5,
                        mb: 1,
                        px: 1,
                        py: 0.5,
                        backgroundColor: alpha(theme.palette.error.light, 0.1),
                        borderRadius: 1,
                        border: `1px dashed ${alpha(theme.palette.error.main, 0.3)}`,
                        width: '100%'
                      }}
                    >
                      <Typography variant="caption" color="error" display="block" fontWeight="medium">
                        Min. Purchase Amount: {formatCurrency(minimumOrderValue)}
                      </Typography>
                      <Typography variant="caption" color="error" display="block" fontWeight="medium">
                        Min. Quantity: {effectiveMinimumQty}
                      </Typography>
                      {/* {(orderType === "MARKET" || orderType === "STOPLOSSMARKET") && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Based on current market price: {formatCurrency(orderSide === "BUY" ? marketData.bestAsk || marketData.ltp : marketData.bestBid || marketData.ltp)}
                        </Typography>
                      )} */}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ my: 1 }}>
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={price || ""}
                    onChange={handlePriceChange}
                    onKeyDown={handlePriceKeyDown}
                    error={priceError}
                    helperText={
                      priceError
                        ? `Value must be a multiple of tick_size (${props.tick_size})`
                        : ""
                    }
                    disabled={
                      orderType === "MARKET" || orderType === "STOPLOSSMARKET"
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            ₹
                          </Typography>
                        </InputAdornment>
                      ),
                      inputProps: {
                        step: props.tick_size,
                        min: 0,
                        style: {
                          textAlign: "left",
                          fontWeight: "bold",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: theme.palette.text.disabled,
                        fontWeight: "bold",
                        backgroundColor: alpha(
                          theme.palette.action.disabledBackground,
                          0.1
                        ),
                        borderRadius: 1,
                        opacity: 0.7,
                        cursor: "not-allowed",
                        textDecoration: "line-through",
                      },
                      "& .MuiOutlinedInput-root.Mui-disabled": {
                        "& > fieldset": {
                          borderColor: alpha(theme.palette.text.disabled, 0.3),
                          borderStyle: "dashed",
                          borderWidth: 1,
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: priceError
                            ? theme.palette.error.main
                            : alpha(theme.palette.text.secondary, 0.3),
                        },
                        "&:hover fieldset": {
                          borderColor: priceError
                            ? theme.palette.error.dark
                            : theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 1.5 : 1.5,
          borderTop: 1,
          borderColor: "divider",
          mt: "auto",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: isMobile ? "100%" : "auto",
            gap: 0.5,
          }}
        >
          <FundsSummary
            requiredMargin={requiredMargin}
            availableFormatted={balanceData.formatted.availableCash}
            isLowMargin={isLowMargin}
            loadingFunds={loadingFunds}
            onRefresh={handleRefreshFunds}
            showMarginRefresh={useServerSideMargin}
            isMarginStale={marginIsStale}
            marginLoading={marginLoading}
            onMarginRefresh={refreshMargin}
            marginErrorMessage={marginHasApiError ? "" : marginErrorMessage}
            marginBlocked={blockedByMargin}
            onAvailableClick={handleAvailableClick}
          />

          {isBrokerageApplied && props.payload?.instrument_name && props.payload.instrument_name.trim() !== '' && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Brokerage Value:
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight="medium"
                  color="text.primary"
                  sx={{ ml: 0.5 }}
                >
                  ₹{brokerageValue.toFixed(2)}
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
            width: isMobile ? "100%" : "auto",
            mt: isMobile ? 1 : 0,
          }}
        >
          {!isMobile && (
            <>
              {/* Add bookmark icon as a standalone button with menu */}
              {props?.formattedWatchlists && props?.formattedWatchlists?.length > 0 && (
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Check if instrument is already in any marketwatch
                      const isInAnyWatchlist = props?.formattedWatchlists?.some(watchlist =>
                        watchlist.instruments?.some(
                          (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                        )
                      );

                      if (isInAnyWatchlist) {
                        // If already in a watchlist, show menu to select which one to remove from
                        setAnchorEl(e.currentTarget);
                      } else {
                        // If not in any watchlist, show menu to select which one to add to
                        setAnchorEl(e.currentTarget);
                      }
                    }}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 0.25,
                      minWidth: 32,
                      height: 28,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    {props?.formattedWatchlists?.some((watchlist) =>
                      watchlist.instruments?.some(
                        (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                      )
                    ) ? (
                      <BookmarkIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <BookmarkBorderIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.text.secondary,
                        }}
                      />
                    )}
                  </IconButton>

                  {/* Menu for selecting marketwatch */}
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                      sx: {
                        maxHeight: 300,
                        width: 250,
                        mt: 1,
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                      {props?.formattedWatchlists?.some((watchlist) =>
                        watchlist.instruments?.some(
                          (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                        )
                      ) ? 'Remove from Marketwatch' : 'Add to Marketwatch'}
                    </Typography>
                    <Divider />
                    {props?.formattedWatchlists?.map((watchlist, index) => {
                      const isInWatchlist = watchlist.instruments?.some(
                        (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                      );

                      return (
                        <MenuItem
                          key={watchlist.id}
                          onClick={() => {
                            if (isInWatchlist) {
                              // Remove from this watchlist
                              handleRemoveFromMarketwatch(watchlist, index);
                            } else {
                              // Add to this watchlist
                              handleAddToMarketwatch(watchlist, index);
                            }
                            setAnchorEl(null);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1
                          }}
                        >
                          <Typography variant="body2">
                            {watchlist.name}
                          </Typography>
                          {isInWatchlist && (
                            <BookmarkIcon
                              fontSize="small"
                              sx={{ color: theme.palette.success.main, ml: 1 }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>
              )}

              {isLowMargin && requiredMargin > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddFundsClick}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.25,
                    height: 28,
                  }}
                >
                  Add Funds
                </Button>
              )}

              <Button
                onClick={handleClose}
                variant="outlined"
                size="small"
                sx={{ minWidth: 80, height: 28, fontSize: '0.8rem' }}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            onClick={handleOrderSubmit}
            variant="contained"
            size={isMobile ? "large" : "small"}
            disabled={
              // If it's a square-off operation, disable if loading, funds insufficient, or pending orders exist (but not for modify operations)
              isSquareOff ? (loading ||
                (availableMargin < requiredMargin && requiredMargin > 0) ||
                (!is_modify && pendingOrders.instrumentPendingOrders && pendingOrders.instrumentPendingOrders.length > 0)) :
                // Normal case: full validation
                (loading || isDisabled || (availableMargin < requiredMargin && requiredMargin > 0) ||
                  (minQuantityError && !isSquareOff))
            }
            fullWidth={isMobile}
            sx={{
              backgroundColor:
                orderSide === "BUY" ? "success.main" : "error.main",
              "&:hover": {
                backgroundColor:
                  orderSide === "BUY" ? "success.dark" : "error.dark",
              },
              height: isMobile ? 'auto' : 28,
              fontSize: isMobile ? 'inherit' : '0.8rem',
            }}
          >
            {loading
              ? "Placing Order..."
              : props.is_modify
                ? `Modify ${orderSide}`
                : orderSide}
          </Button>

          {/* Add Funds button for mobile */}
          {isMobile && isLowMargin && requiredMargin > 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddFundsClick}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                py: 0.5,
                mt: 1,
                width: '100%',
              }}
            >
              Add Funds
            </Button>
          )}

          {/* Add bookmark icon for mobile with menu */}
          {isMobile && props?.formattedWatchlists && props?.formattedWatchlists?.length > 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Open the mobile dialog for selecting marketwatch
                  setMobileDialogOpen(true);
                }}
              >
                {props?.formattedWatchlists.some((watchlist) =>
                  watchlist.instruments?.some(
                    (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                  )
                ) ? (
                  <>
                    <BookmarkIcon
                      fontSize="small"
                      sx={{ color: theme.palette.success.main }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      In Marketwatch - Tap to manage
                    </Typography>
                  </>
                ) : (
                  <>
                    <BookmarkBorderIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Add to Marketwatch
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* Mobile Dialog for selecting marketwatch */}
          <Dialog
            open={mobileDialogOpen}
            onClose={() => setMobileDialogOpen(false)}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                borderRadius: 2,
                maxHeight: '70vh'
              }
            }}
          >
            <DialogTitle>
              {props?.formattedWatchlists?.some((watchlist) =>
                watchlist.instruments?.some(
                  (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                )
              ) ? 'Manage Marketwatch' : 'Add to Marketwatch'}
              <IconButton
                aria-label="close"
                onClick={() => setMobileDialogOpen(false)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <List sx={{ pt: 0 }}>
                {props?.formattedWatchlists?.map((watchlist, index) => {
                  const isInWatchlist = watchlist.instruments?.some(
                    (instr) => String(instr.token) === String(props.token) && String(instr.exchange) === String(props.exchange)
                  );

                  return (
                    <ListItem
                      key={watchlist.id}
                      button
                      onClick={() => {
                        if (isInWatchlist) {
                          // Remove from this watchlist
                          handleRemoveFromMarketwatch(watchlist, index);
                        } else {
                          // Add to this watchlist
                          handleAddToMarketwatch(watchlist, index);
                        }
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        backgroundColor: isInWatchlist ? alpha(theme.palette.success.main, 0.1) : 'transparent'
                      }}
                    >
                      <ListItemText
                        primary={watchlist.name}
                        primaryTypographyProps={{
                          fontWeight: isInWatchlist ? 600 : 400
                        }}
                      />
                      {isInWatchlist ? (
                        <BookmarkIcon
                          fontSize="small"
                          sx={{ color: theme.palette.success.main }}
                        />
                      ) : (
                        <BookmarkBorderIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMobileDialogOpen(false)}>
                Done
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </DialogActions>

      {/* Funds Menu Overlay and Modals */}
      <FundsMenuOverlay
        isMobile={isMobile}
        anchorEl={fundsPopoverAnchor}
        mobileOpen={mobileFundsOpen}
        onClose={() => {
          setFundsPopoverAnchor(null);
          setMobileFundsOpen(false);
        }}
        balanceData={balanceData}
        onWithdrawClick={() => setIsWithdrawModalOpen(true)}
        onAddFundsClick={() => setIsAddFundsModalOpen(true)}
        onRefreshClick={handleRefreshFunds}
      />
      <WithdrawModal open={isWithdrawModalOpen} onClose={() => setIsWithdrawModalOpen(false)} />
      <AddFundsModal
        open={isAddFundsModalOpen}
        onClose={() => setIsAddFundsModalOpen(false)}
        ref={payInComponentRef}
        setIsPaymentGuidelinesModalOpen={setIsPaymentGuidelinesModalOpen}
      />
      <PaymentGuidelinesModal
        open={isPaymentGuidelinesModalOpen}
        onClose={() => setIsPaymentGuidelinesModalOpen(false)}
      />
    </Dialog>
  );
});

OrderWindow.displayName = "OrderWindow";

export default OrderWindow;
